<template>
  <div class="flex flex-col gap-4">
    <div v-if="profile.attributes.status !== ProfileRecord.STATUS_ADDED">
      <p class="font-medium">
        <span v-if="profile.attributes.status === ProfileRecord.STATUS_INVITED">
          {{ $t('profile.labels.explainerUpdateEmailInvited', { name: profile.attributes.name }) }}
        </span>
        <span v-else-if="profile.attributes.status === ProfileRecord.STATUS_ACTIVE">
          {{ $t('profile.labels.explainerUpdateEmailActive', { name: profile.attributes.name }) }}
        </span>
      </p>
    </div>
    <div v-if="profile.attributes.email_address">
      <p class="mb-1 text-sm font-medium text-gray-700">{{ $t('profile.attributes.email_address_old') }}</p>
      <p class="text-sm text-gray-600">{{ email_address_old }}</p>
    </div>
    <div>
      <label
        class="mb-1 block text-sm font-medium text-gray-700"
        for="contact-new-email-address"
      >
        {{ `${$t('profile.attributes.email_address_new')} *` }}
      </label>
      <InputText
        id="contact-new-email-address"
        v-model.trim="v$.email_address_new.$model"
        class="w-full"
        :invalid="v$.email_address_new.$error && v$.$dirty"
        data-test="contact-new-email-address"
        @input="v$.email_address_new.$model = $event.target.value.toLowerCase()"
      />
      <div v-if="v$.email_address_new.$error && v$.$dirty">
        <p
          v-if="v$.email_address_new.required.$invalid"
          class="mt-1 text-sm text-red-500"
        >
          {{ $t('profile.validation.requireEmailAddress') }}
        </p>
        <p
          v-if="v$.email_address_new.email.$invalid"
          class="mt-1 text-sm text-red-500"
        >
          {{ $t('profile.validation.mustBeEmailAddress') }}
        </p>
        <p
          v-if="
            v$.email_address_new.emailAddressDifferent.$invalid ||
            v$.email_address_new.emailAddressNotAvailable.$invalid
          "
          class="mt-1 text-sm text-red-500"
        >
          {{ $t('profile.validation.emailTaken') }}
        </p>
        <p
          v-if="v$.email_address_new.emailAddressDemo.$invalid"
          class="mt-1 text-sm text-red-500"
        >
          Email address of a contact in this demo client must end with '{{ email_address_suffix }}' or '@ziggu.io'
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import server_validation from '@/app/util/server_validation';
import { ProfileRecord, RECORD_TYPE, Tenant } from '@/app/data/model_constants';
import { mapActions, mapGetters } from 'vuex';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { email } from '@/app/util/validators';

export default {
  name: 'TheUpdateEmailAddressModal',

  mixins: [server_validation.createMixin()],

  props: {
    profile: { type: Object, required: true },
    email_address_old: { type: String, required: false, default: '' },
  },

  emits: ['close', 'updateEmailAddress'],

  setup() {
    return { v$: useVuelidate() };
  },

  data() {
    return {
      email_address_new: null,
      ProfileRecord,
    };
  },

  validations: {
    email_address_new: {
      required,
      email,
      emailAddressDifferent(val) {
        return val !== this.email_address_old;
      },
      emailAddressNotAvailable: server_validation.validator('not_available'),
      emailAddressDemo(email_address) {
        return (
          !email_address ||
          this.current_client.attributes.status !== Tenant.STATUS_DEMO ||
          email_address.endsWith(this.email_address_suffix) ||
          email_address.endsWith('@ziggu.io')
        );
      },
    },
  },

  computed: {
    ...mapGetters(['current_client', 'current_profile']),
    email_address_suffix() {
      return this.current_client.attributes.status === Tenant.STATUS_DEMO
        ? `@${this.current_client.attributes.subdomain}.ziggu.app`
        : '';
    },
  },

  methods: {
    ...mapActions(['FETCH_USER_PROFILE', 'UPDATE_CUSTOMER', 'UPDATE_EMPLOYEE', 'UPDATE_PARTNER', 'UPDATE_LEAD']),

    async submit() {
      this.clearServerErrors();
      this.v$.$touch();
      if (this.v$.$invalid) return;
      const profile = _.cloneDeep(this.profile);
      profile.attributes.email_address = this.email_address_new;

      const update_actions = {
        [RECORD_TYPE.EMPLOYEE]: this.UPDATE_EMPLOYEE,
        [RECORD_TYPE.CUSTOMER]: this.UPDATE_CUSTOMER,
        [RECORD_TYPE.PARTNER]: this.UPDATE_PARTNER,
        [RECORD_TYPE.LEAD]: this.UPDATE_LEAD,
      };

      try {
        await update_actions[this.profile.type]({ record: profile });
        if (this.current_profile.id === profile.id) await this.FETCH_USER_PROFILE({});
        this.$emit('updateEmailAddress', this.email_address_new);
        this.$emit('close');
      } catch (error) {
        const response_status = _.get(error, 'response.status');
        if (response_status === 422) {
          this.addServerError('not_available');
          return;
        }
        throw new Error(`${this.profile.attributes.email_address} failed to update`, { cause: error });
      }
    },
  },
};
</script>
